import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, SongAlbumCard, DateTimePicker } from '@common/musicHubCommon';
import { ROUTES } from '../../../routes';
import './index.scss';

interface AlbumData {
  albumName: string;
  coverArtPreview: string | null;
  artists: string[];
  genres: string[];
  moods: string[];
  caption: string;
  upc: string;
}

type ReleaseType = 'now' | 'scheduled';

const AlbumSchedule: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // 从 sessionStorage 获取专辑信息
  const albumData: AlbumData | null = useMemo(() => {
    const stored = sessionStorage.getItem('albumData');
    return stored ? JSON.parse(stored) : null;
  }, []);

  // 从 sessionStorage 获取歌曲数量
  const songsCount = useMemo(() => {
    const stored = sessionStorage.getItem('albumSongs');
    return stored ? JSON.parse(stored).length : 0;
  }, []);

  // 检查是否有专辑信息
  useEffect(() => {
    if (!albumData) {
      navigate(ROUTES.albumCreate);
    }
  }, [albumData, navigate]);

  // 表单数据
  const [releaseType, setReleaseType] = useState<ReleaseType>('now');
  const [scheduledDate, setScheduledDate] = useState<Date | null>(null);
  const [artistMessage, setArtistMessage] = useState('');

  // 处理返回
  const handleBack = () => {
    navigate(ROUTES.albumEditReorder);
  };

  // 处理艺术家留言
  const handleMessageChange = (value: string) => {
    if (value.length <= 500) {
      setArtistMessage(value);
    }
  };

  // 表单验证
  const isFormValid = () => {
    if (!releaseType) return false;
    if (releaseType === 'scheduled' && !scheduledDate) return false;
    return true;
  };

  // 处理提交
  const handleSubmit = () => {
    if (isFormValid()) {
      // 保存发布信息到 sessionStorage
      sessionStorage.setItem('albumScheduleInfo', JSON.stringify({
        releaseType,
        scheduledDate: scheduledDate?.toISOString() || null,
        artistMessage,
      }));
      navigate(ROUTES.albumSuccess);
    }
  };

  if (!albumData) {
    return null;
  }

  const validGenres = albumData.genres.filter(g => g.trim() !== '');

  return (
    <div className="album-schedule">
      {/* 页面标题 */}
      <div className="album-schedule__header">
        <button className="back-btn" onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
          <span>Release Album</span>
        </button>
      </div>

      {/* 主内容 */}
      <div className="album-schedule__content">
        <div className="album-schedule__card">
          {/* 标题 */}
          <h2 className="album-schedule__title">Choose Your Release</h2>

          {/* 专辑预览卡片 */}
          <div className="album-preview">
            <SongAlbumCard
              coverUrl={albumData.coverArtPreview || undefined}
              title={albumData.albumName}
              artist={albumData.artists[0]}
              trackCount={songsCount}
              tags={validGenres}
            />
          </div>

          {/* 表单 */}
          <Form form={form} layout="vertical" className="album-schedule__form">
            {/* From the Artist */}
            <Form.Item 
              label={
                <div className="form-label-with-counter">
                  <span>From the Artist</span>
                  <span className="counter">{artistMessage.length}/500</span>
                </div>
              }
              className="form-item"
            >
              <Input.TextArea
                value={artistMessage}
                onChange={(e) => handleMessageChange(e.target.value)}
                placeholder="Share the story behind your album, production details, or any message for your listeners..."
                rows={3}
              />
            </Form.Item>

            {/* Release 选项 */}
            <Form.Item 
              label="Release"
              required
              className="form-item"
            >
              <div className="release-options">
                {/* Release Now */}
                <div
                  className={`release-option ${releaseType === 'now' ? 'release-option--selected' : ''}`}
                  onClick={() => setReleaseType('now')}
                >
                  <div className="release-option__radio">
                    <div className={`radio-circle ${releaseType === 'now' ? 'radio-circle--checked' : ''}`}>
                      {releaseType === 'now' && <div className="radio-circle__inner" />}
                    </div>
                  </div>
                  <div className="release-option__content">
                    <span className="release-option__title">Release Now</span>
                    <span className="release-option__desc">Your album will be available immediately</span>
                  </div>
                </div>

                {/* Preview Start (Scheduled) */}
                <div
                  className={`release-option ${releaseType === 'scheduled' ? 'release-option--selected' : ''}`}
                >
                  <div 
                    className="release-option__header"
                    onClick={() => setReleaseType('scheduled')}
                  >
                    <div className="release-option__radio">
                      <div className={`radio-circle ${releaseType === 'scheduled' ? 'radio-circle--checked' : ''}`}>
                        {releaseType === 'scheduled' && <div className="radio-circle__inner" />}
                      </div>
                    </div>
                    <div className="release-option__content">
                      <span className="release-option__title">Preview Start</span>
                      <span className="release-option__desc">Schedule your release for a specific date and time</span>
                    </div>
                  </div>

                  {/* 日期选择器 */}
                  {releaseType === 'scheduled' && (
                    <div className="release-option__datepicker">
                      <DateTimePicker
                        value={scheduledDate}
                        onChange={setScheduledDate}
                        placeholder="Select time"
                      />
                    </div>
                  )}
                </div>
              </div>
            </Form.Item>

            {/* 提交按钮 */}
            <Button
              type="primary"
              disabled={!isFormValid()}
              onClick={handleSubmit}
              className={`album-schedule__submit ${!isFormValid() ? 'album-schedule__submit--disabled' : ''}`}
            >
              Submit Release
            </Button>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default AlbumSchedule;
